import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, MessageSquare } from 'lucide-react';
import { messageQueueService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { MessageQueue, MessageQueueFilters } from '../types/messageQueue';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import MessageQueueList from '../components/message-queues/MessageQueueList';
import MessageQueueForm from '../components/message-queues/MessageQueueForm';
import MessageQueueFilters from '../components/message-queues/MessageQueueFilters';

type ModalType = 'create' | 'edit' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

function MessageQueues() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingQueue, setEditingQueue] = useState<MessageQueue | null>(null);
  const [filters, setFilters] = useState<MessageQueueFilters>({});
  const queryClient = useQueryClient();

  // Queries
  const { data: queues, isLoading } = useQuery({
    queryKey: ['message-queues', filters],
    queryFn: () => messageQueueService.getQueues(),
  });

  const { data: integrations } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => messageQueueService.getIntegrations(),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<MessageQueue>) => messageQueueService.createQueue(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message-queues'] });
      messageHandler.success('Fila de mensagens criada com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: MessageQueue) => messageQueueService.updateQueue(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message-queues'] });
      messageHandler.success('Fila de mensagens atualizada com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => messageQueueService.deleteQueue(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message-queues'] });
      messageHandler.success('Fila de mensagens excluída com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  // Handlers
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    // Extract subjects from form data
    const subjectsEntries = Array.from(formData.entries())
      .filter(([key]) => key.startsWith('subjects['))
      .map(([_, value]) => value as string);

    const data = {
      name: formData.get('name') as string,
      subjects: subjectsEntries,
      integration_id: formData.get('integration_id')
        ? Number(formData.get('integration_id'))
        : null,
      integration_key: formData.get('integration_key')
        ? (formData.get('integration_key') as string)
        : '',
      default: formData.get('default') === 'on',
      screening: formData.get('screening') === 'on',
      show_user: formData.get('show_user') === 'on',
      ia_respond: formData.get('ia_respond') === 'on',
      timeout_queue_id: formData.get('timeout_queue_id')
        ? Number(formData.get('timeout_queue_id'))
        : null,
      timeout_messages: formData.get('timeout_messages') === 'on',
      pabx_refer: formData.get('pabx_refer')
        ? (formData.get('pabx_refer') as string)
        : null,
      timeout_queue: formData.get('timeout_queue')
        ? Number(formData.get('timeout_queue'))
        : 20,
    };

    if (editingQueue) {
      updateMutation.mutate({ ...editingQueue, ...data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (queue: MessageQueue) => {
    messageHandler.confirm(
      'Excluir Fila',
      'Tem certeza que deseja excluir esta fila de mensagens?',
      () => deleteMutation.mutate(queue.id)
    );
  };

  const handleFilterChange = (newFilters: MessageQueueFilters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({});
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingQueue(null);
  };

  // Filter queues based on filters
  const filteredQueues =
    queues?.filter((queue: MessageQueue) => {
      if (filters.name && !queue.name.toLowerCase().includes(filters.name.toLowerCase())) {
        return false;
      }
      if (filters.integration_id && queue.integration_id !== filters.integration_id) {
        return false;
      }
      if (filters.default && !queue.default) {
        return false;
      }
      if (filters.screening && !queue.screening) {
        return false;
      }
      if (filters.show_user === false && queue.show_user !== false) {
        return false;
      }
      return true;
    }) || [];

  const isFiltered = Object.keys(filters).length > 0;

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Filas de Atendimento"
          description="Gerencie as filas de atendimento para organizar o atendimento."
          actions={
            <Button
              onClick={() => {
                setEditingQueue(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Fila
            </Button>
          }
        />

        <MessageQueueFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          integrations={integrations || []}
        />

        <MessageQueueList
          queues={filteredQueues}
          onEdit={queue => {
            setEditingQueue(queue);
            setModal({ type: 'edit' });
          }}
          onDelete={handleDelete}
          isLoading={isLoading}
          isFiltered={isFiltered}
        />

        {/* Create/Edit Modal */}
        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingQueue ? 'Editar Fila de Mensagens' : 'Nova Fila de Mensagens'}
          size="lg"
        >
          <MessageQueueForm
            queue={editingQueue}
            integrations={integrations || []}
            allQueues={queues || []}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>
      </div>
    </div>
  );
}

export default MessageQueues;
